// ./src/components/features/QrTags/QrTagsFilterBar/QrTagsFilterBar.tsx
//
// ROADMAP #172 — filters for an event's QR tags: who holds them (somebody / nobody yet) and, on an event with sessions or
// dates, which one they are for. Fully controlled — the page maps the value to a QrTagFilter (both server-side).
import React from "react";
import { Group, Button, Box, Stack } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { AppIcon, SelectField } from "@/components/generic";

export type QrTagHeldFilter = "held" | "free" | null;

export interface QrTagsFilterValue {
  held: QrTagHeldFilter;
  eventTimeSlotId: string | null;
}

export interface QrTagsFilterBarProps {
  value: QrTagsFilterValue;
  onChange: (value: QrTagsFilterValue) => void;
  /** The event's sessions / dates; none → no "For" filter. */
  slotOptions?: { value: string; label: string }[];
  disabled?: boolean;
  showClear?: boolean;
  vertical?: boolean;
}

export const EMPTY_QR_TAGS_FILTER: QrTagsFilterValue = { held: null, eventTimeSlotId: null };

export const countQrTagFilters = (value: QrTagsFilterValue): number => [value.held, value.eventTimeSlotId].filter(Boolean).length;

const FIELD_STYLE: React.CSSProperties = { flex: "1 1 200px", minWidth: 190 };

const QrTagsFilterBarComponent: React.FC<QrTagsFilterBarProps> = ({ value, onChange, slotOptions = [], disabled = false, showClear = true, vertical = false }) => {
  const { t } = useTranslation(["qrTags", "common"]);

  const heldControl = (
    <Box style={vertical ? undefined : FIELD_STYLE}>
      <SelectField
        label={t("qrTags:filters.held.label", { defaultValue: "Given out" })}
        placeholder={t("qrTags:filters.held.placeholder", { defaultValue: "Any" })}
        data={[
          { value: "held", label: t("qrTags:filters.held.held", { defaultValue: "Held by somebody" }) },
          { value: "free", label: t("qrTags:filters.held.free", { defaultValue: "Not given out yet" }) },
        ]}
        value={value.held}
        onChange={(_n, v) => onChange({ ...value, held: (v as QrTagHeldFilter) ?? null })}
        clearable
        disabled={disabled}
        size="md"
      />
    </Box>
  );

  const slotControl =
    slotOptions.length > 0 ? (
      <Box style={vertical ? undefined : FIELD_STYLE}>
        <SelectField
          label={t("qrTags:filters.slot.label", { defaultValue: "For" })}
          placeholder={t("qrTags:filters.slot.placeholder", { defaultValue: "Any date or session" })}
          data={slotOptions}
          value={value.eventTimeSlotId}
          onChange={(_n, v) => onChange({ ...value, eventTimeSlotId: v })}
          clearable
          searchable
          disabled={disabled}
          size="md"
        />
      </Box>
    ) : null;

  if (vertical) {
    return (
      <Stack gap="md">
        {heldControl}
        {slotControl}
      </Stack>
    );
  }

  return (
    <Group gap="sm" align="flex-end" wrap="wrap">
      {heldControl}
      {slotControl}

      {countQrTagFilters(value) > 0 && showClear && (
        <Button
          variant="subtle"
          color="gray"
          size="md"
          onClick={() => onChange(EMPTY_QR_TAGS_FILTER)}
          disabled={disabled}
          leftSection={<AppIcon iconName="X" size={16} />}
        >
          {t("common:buttons.clear", { defaultValue: "Clear" })}
        </Button>
      )}
    </Group>
  );
};

export const QrTagsFilterBar = React.memo(QrTagsFilterBarComponent);
